import { useState, useEffect } from 'react';
import './App.css';
import { get_token_balance, buy_main, sell_main, get_publicKey, sell_approve, sell, partial_sell } from './main.js';

// Debounce function
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    // 如果正在执行中，直接返回
    if (timeoutId) {
      console.log('⚠️ 操作正在进行中，请勿重复点击');
      return;
    }
    
    // 设置定时器
    timeoutId = setTimeout(() => {
      func.apply(this, args);
      timeoutId = null; // 执行完成后重置定时器ID
    }, delay);
  };
}

function App() {
  // 添加状态管理
  const [privateKey, setPrivateKey] = useState('');
  const [rpcUrl, setRpcUrl] = useState('');
  const [tokenAddress, setTokenAddress] = useState('');
  const [consoleOutput, setConsoleOutput] = useState([]);
  const [buyAmount, setBuyAmount] = useState('');
  const [sellAmount, setSellAmount] = useState('');

  // Add new state variables for tracking transaction status
  const [isBuying, setIsBuying] = useState(false);
  const [isSelling, setIsSelling] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  // 添加白名单列表
  const whitelistedAddresses = [
    //单地址
    "0x312c0afb3673809239d292c889711a76396e6809",
    "0xe1d6ffe3FA233Cf69101c69c2f760C3222f8a4aE",
    "0xb065735b368980dfa948a7e0b61fb0e1c5d89ef1",
    "0xacD6589D12cF1a3Fac144e60Edd5554d770f4926",
    "0x7a096b84d2B1343ECe30A2ffe55D7d6b077393d5",
    "0xf4c37E896DdF36e3Bf2085c417C355f54eb135Ad",
    "0xd174b4f4c03f620733549f29ef5d96ee9ff82d72",
    "0xeda0527b1d577c6e6e5ee3d0ddd86392217212fd",
    "0x54c5e0409315c8d62dd3dcd842a55fd70b0d5ba2",
    "0x456..."  // 继续添加地址
  ];

  // 添加检查白名单的函数
  const checkWhitelist = async () => {
    try {
      if (!privateKey || !rpcUrl) {
        console.log('请先填写私钥和RPC地址');
        return false;
      }
      
      const publicKey = await get_publicKey(privateKey, rpcUrl);
      const publicKeyLower = publicKey.toLowerCase();
      const isWhitelisted = whitelistedAddresses.map(addr => addr.toLowerCase()).includes(publicKeyLower);
      
      if (!isWhitelisted) {
        console.log('💔果咩那塞，您没有取得白名单权限');
      }
      
      return isWhitelisted;
    } catch (error) {
      console.log('检查白名单时出错:', error.message);
      return false;
    }
  };

  // 修改 useEffect
  useEffect(() => {
    // 保存原始的 console.log
    const originalConsoleLog = console.log;
    
    // 定义要过滤的消息列表
    const filteredMessages = ['0x1', 'chainChanged', '0x1 0x1'];
    
    // 创建一个新的 console.log 函数
    console.log = (...args) => {
      // 检查是否是需要过滤的消息
      const message = args.join(' ');
      if (filteredMessages.includes(message) || filteredMessages.includes(args[0])) {
        return;
      }
      
      // 对于其他日志，添加到控制台输出并使用原始的 console.log
      setConsoleOutput(prev => {
        const newLog = args.join(' ');
        // 避免重复日志
        if (prev.length > 0 && prev[prev.length - 1] === newLog) {
          return prev;
        }
        return [...prev, newLog];
      });
      
      // 使用原始的 console.log
      originalConsoleLog.apply(console, args);
    };

    // 清理函数
    return () => {
      console.log = originalConsoleLog;
    };
  }, []); 

  // 修改处理查询余额的函数
  const handleCheckBalance = async () => {
    if (!await checkWhitelist()) return;
    
    try {
      const abi = ["function balanceOf(address account) view returns (uint256)"];
      await get_token_balance(tokenAddress, abi, privateKey, rpcUrl);
    } catch (error) {
      console.log('Error:', error.message);
    }
  };

  // 修改购买处理函数
  const handleBuy = async () => {
    // 如果已经在进行购买，直接返回
    if (isBuying) {
      console.log('⚠️ 正在执行购买操作，请勿重复点击');
      return;
    }

    if (!await checkWhitelist()) return;
    
    try {
      if (!privateKey || !rpcUrl || !tokenAddress || !buyAmount) {
        console.log('❓错误: 您是不是忘了填什么东西');
        return;
      }

      setIsBuying(true); // 设置购买状态为true
      console.log('开始购买操作...');
      console.log('买入代币地址:', tokenAddress);
      console.log('支付数量:', buyAmount, 'Virtual');

      await buy_main(
        undefined,
        undefined,
        privateKey,
        rpcUrl,
        buyAmount,
        tokenAddress
      );
    } catch (error) {
      console.log('购买失败:', error.message);
      if (error.error) {
        console.log('详细错误:', error.error);
      }
    } finally {
      setIsBuying(false); // 无论成功失败都重置状态
    }
  };

  // 修改卖出处理函数
  const handleSellOnly = async () => {
    if (isSelling) {
      console.log('⚠️ 正在执行卖出操作，请勿重复点击');
      return;
    }

    if (!await checkWhitelist()) return;
    
    try {
      if (!privateKey || !rpcUrl || !tokenAddress || !sellAmount) {
        console.log('❓错误: 您是不是忘了填什么东西');
        return;
      }

      setIsSelling(true);
      await sell(
        undefined, // pool_addr
        undefined, // abi
        privateKey,
        rpcUrl,
        sellAmount,
        tokenAddress
      );
    } catch (error) {
      console.log('卖出失败，代币可能未授权:', error.message);
      if (error.error) {
        console.log('详细错误:', error.error);
      }
    } finally {
      setIsSelling(false);
    }
  };

  // 修改卖出处理函数
  const handleSell = async () => {
    if (isSelling) {
      console.log('⚠️ 正在执行卖出操作，请勿重复点击');
      return;
    }

    if (!await checkWhitelist()) return;
    
    try {
      setIsSelling(true);
      await sell_main(
        tokenAddress,
        sellAmount,
        privateKey,
        rpcUrl
      );
    } catch (error) {
      console.log('Error:', error.message);
    } finally {
      setIsSelling(false);
    }
  };

  // Add new handler for pre-approval
  const handlePreApprove = async () => {
    if (isApproving) {
      console.log('⚠️ 正在执行授权操作，请勿重复点击');
      return;
    }

    if (!await checkWhitelist()) return;
    
    try {
      if (!privateKey || !rpcUrl || !tokenAddress) {
        console.log('❓错误: 请先填写私钥、RPC地址和代币地址');
        return;
      }

      setIsApproving(true);
      console.log('开始授权操作...');
      await sell_approve(
        tokenAddress,
        undefined, // 使用默认 ABI
        privateKey,
        rpcUrl
      );
    } catch (error) {
      console.log('授权失败:', error.message);
      if (error.error) {
        console.log('详细错误:', error.error);
      }
    } finally {
      setIsApproving(false);
    }
  };

  // Add new handler for "一键全卖"
  const handleSellAll = async () => {
    if (isSelling) {
      console.log('⚠️ 正在执行卖出操作，请勿重复点击');
      return;
    }

    if (!await checkWhitelist()) return;

    try {
      setIsSelling(true);
      console.log('开始执行一键全卖操作...');
      await partial_sell(
        undefined, // pool_addr
        undefined, // abi
        privateKey,
        rpcUrl,
        1, // Sell 100%
        tokenAddress
      );
    } catch (error) {
      console.log('一键全卖失败:', error.message);
      if (error.error) {
        console.log('详细错误:', error.error);
      }
    } finally {
      setIsSelling(false);
    }
  };

  // Add new handler for "卖50%"
  const handleSellHalf = async () => {
    if (isSelling) {
      console.log('⚠️ 正在执行卖出操作，请勿重复点击');
      return;
    }

    if (!await checkWhitelist()) return;

    try {
      setIsSelling(true);
      console.log('开始执行卖出50%操作...');
      await partial_sell(
        undefined, // pool_addr
        undefined, // abi
        privateKey,
        rpcUrl,
        0.5, // Sell 50%
        tokenAddress
      );
    } catch (error) {
      console.log('卖出50%失败:', error.message);
      if (error.error) {
        console.log('详细错误:', error.error);
      }
    } finally {
      setIsSelling(false);
    }
  };

  // Add new handler for "卖30%"
  const handleSellThirty = async () => {
    if (isSelling) {
      console.log('⚠️ 正在执行卖出操作，请勿重复点击');
      return;
    }

    if (!await checkWhitelist()) return;

    try {
      setIsSelling(true);
      console.log('开始执行卖出30%操作...');
      await partial_sell(
        undefined, // pool_addr
        undefined, // abi
        privateKey,
        rpcUrl,
        0.3, // Sell 30%
        tokenAddress
      );
    } catch (error) {
      console.log('卖出30%失败:', error.message);
      if (error.error) {
        console.log('详细错误:', error.error);
      }
    } finally {
      setIsSelling(false);
    }
  };

  // Add new handler for "卖70%"
  const handleSellSeventy = async () => {
    if (isSelling) {
      console.log('⚠️ 正在执行卖出操作，请勿重复点击');
      return;
    }

    if (!await checkWhitelist()) return;

    try {
      setIsSelling(true);
      console.log('开始执行卖出70%操作...');
      await partial_sell(
        undefined, // pool_addr
        undefined, // abi
        privateKey,
        rpcUrl,
        0.7, // Sell 70%
        tokenAddress
      );
    } catch (error) {
      console.log('卖出70%失败:', error.message);
      if (error.error) {
        console.log('详细错误:', error.error);
      }
    } finally {
      setIsSelling(false);
    }
  };

  // Add new handler for "一键授权+全卖"
  const handleApproveAndSellAll = async () => {
    if (isSelling) {
      console.log('⚠️ 正在执行卖出操作，请勿重复点击');
      return;
    }

    if (!await checkWhitelist()) return;
    
    try {
      if (!privateKey || !rpcUrl || !tokenAddress) {
        console.log('❓错误: 请先填写私钥、RPC地址和代币地址');
        return;
      }

      setIsSelling(true);
      console.log('开始获取代币余额...');
      
      // 获取代币余额
      const abi = ["function balanceOf(address account) view returns (uint256)"];
      const balance = await get_token_balance(tokenAddress, abi, privateKey, rpcUrl);
      
      if (balance && balance > 0) {
        console.log('开始执行授权操作...');
        await sell_approve(
          tokenAddress,
          undefined, // 使用默认 ABI
          privateKey,
          rpcUrl
        );
        console.log('授权完成，开始执行卖出操作...');
        await sell(
          undefined, // pool_addr
          undefined, // abi
          privateKey,
          rpcUrl,
          balance, // 使用获取到的余额作为卖出数量
          tokenAddress
        );
      } else {
        console.log('❌ 代币余额为0或获取余额失败');
      }
    } catch (error) {
      console.log('操作失败:', error.message);
      if (error.error) {
        console.log('详细错误:', error.error);
      }
    } finally {
      setIsSelling(false);
    }
  };

  // 更新按钮样式
  const buttonStyle = {
    backgroundColor: '#FFB6C1',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '20px',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 'bold',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    margin: '0 10px',
    ':hover': {
      backgroundColor: '#FF69B4',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    }
  };

  // 添加输入框样式
  const inputStyle = {
    padding: '8px 12px',
    border: '2px solid #FFB6C1',
    borderRadius: '15px',
    outline: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: '#FFF0F5',
    color: '#444',
    fontSize: '14px',
    width: 'calc(100% - 80px)',
    '::placeholder': {
      color: '#FFB6C1'
    }
  };

  // 添加标签样式
  const labelStyle = {
    color: '#FF69B4',
    fontWeight: 'bold',
    marginRight: '10px'
  };

  // 更新控制台样式
  const consoleStyle = {
    marginTop: '20px',
    padding: '20px',
    backgroundColor: '#FFF0F5',
    borderRadius: '15px',
    minHeight: '200px',
    maxHeight: '400px',
    overflow: 'auto',
    border: '2px solid #FFB6C1',
    boxShadow: '0 4px 8px rgba(255, 182, 193, 0.2)'
  };

  // 更新控制台输出文本样式
  const consoleTextStyle = {
    fontFamily: 'monospace',
    padding: '4px 8px',
    margin: '4px 0',
    borderRadius: '8px',
    backgroundColor: '#FFE4E1',
    color: '#444'
  };

  // Update the button styles to show disabled state
  const getButtonStyle = (isDisabled) => ({
    ...buttonStyle,
    backgroundColor: isDisabled ? '#D3D3D3' : '#FFB6C1',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.7 : 1,
  });

  // 通用的卡片样式
  const cardStyle = {
    padding: '10px',
    backgroundColor: '#FFF0F5',
    borderRadius: '15px',
    border: '2px solid #FFB6C1',
    marginBottom: '20px'
  };

  // 更新输入组样式
  const inputGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  };

  // 添加输入行样式
  const inputRowStyle = {
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  };

  return (
    <div className="App" style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* 将私钥和RPC地址输入框放在布局的最上 */}
      <div style={{ ...inputGroupStyle, marginBottom: '20px' }}>
        <div style={inputRowStyle}>
          <label style={{ ...labelStyle, minWidth: '60px' }}>私钥: </label>
          <input 
            type="password" 
            style={inputStyle} 
            value={privateKey}
            onChange={(e) => setPrivateKey(e.target.value)}
            placeholder="请输入您的私钥"
          />
        </div>
        <div style={inputRowStyle}>
          <label style={{ ...labelStyle, minWidth: '60px' }}>RPC地址: </label>
          <input 
            type="text" 
            style={inputStyle} 
            value={rpcUrl}
            onChange={(e) => setRpcUrl(e.target.value)}
            placeholder="请输入RPC地址"
          />
        </div>
      </div>

      {/* 交易信息卡片 */}
      <div style={cardStyle}>
        <h3 style={{ color: '#FF69B4', margin: '0 0 20px 0' }}>交易配置</h3>
        <div style={inputGroupStyle}>
          <div>
            <div style={inputRowStyle}>
              <label style={{ ...labelStyle, minWidth: '80px' }}>代币地址: </label>
              <input 
                type="text" 
                style={inputStyle}
                value={tokenAddress}
                onChange={(e) => setTokenAddress(e.target.value)}
                placeholder="请输入代币合约地址"
              />
            </div>
            <div style={{ marginTop: '10px', textAlign: 'left' }}>
              <button 
                style={buttonStyle} 
                onClick={handleCheckBalance}
              >
                查询余额
              </button>
            </div>
          </div>

          <div>
            <div style={inputRowStyle}>
              <label style={{ ...labelStyle, minWidth: '80px' }}>买入数量: </label>
              <input 
                type="text" 
                style={inputStyle}
                value={buyAmount}
                onChange={(e) => setBuyAmount(e.target.value)}
                placeholder="请输入Virtual数量"
              />
            </div>
            <div style={{ marginTop: '10px', textAlign: 'left' }}>
              <button 
                style={getButtonStyle(isBuying)} 
                onClick={handleBuy}
                disabled={isBuying}
              >
                {isBuying ? '购买中...' : '买入'}
              </button>
            </div>
          </div>

          <div>
            <div style={inputRowStyle}>
              <label style={{ ...labelStyle, minWidth: '80px' }}>卖出数量: </label>
              <input 
                type="text" 
                style={inputStyle}
                value={sellAmount}
                onChange={(e) => setSellAmount(e.target.value)}
                placeholder="请输入Token数量"
              />
            </div>
            <div style={{ marginTop: '10px', textAlign: 'left' }}>
              <button 
                style={getButtonStyle(isApproving)} 
                onClick={handlePreApprove}
                disabled={isApproving}
              >
                {isApproving ? '授权中...' : '提前授权'}
              </button>
              <button 
                style={getButtonStyle(isSelling)} 
                onClick={handleSellOnly}
                disabled={isSelling}
              >
                {isSelling ? '卖出中...' : '卖出指定数量'}
              </button>
              <button 
                style={getButtonStyle(isSelling)} 
                onClick={handleSellAll}
                disabled={isSelling}
              >
                {isSelling ? '全卖中...' : '一键全卖'}
              </button>
              <button 
                style={getButtonStyle(isSelling)} 
                onClick={handleSellThirty}
                disabled={isSelling}
              >
                {isSelling ? '卖出中...' : '卖30%'}
              </button>
              <button 
                style={getButtonStyle(isSelling)} 
                onClick={handleSellHalf}
                disabled={isSelling}
              >
                {isSelling ? '卖出中...' : '卖50%'}
              </button>
              <button 
                style={getButtonStyle(isSelling)} 
                onClick={handleSellSeventy}
                disabled={isSelling}
              >
                {isSelling ? '卖出中...' : '卖70%'}
              </button>
              <button 
                style={getButtonStyle(isSelling)} 
                onClick={handleApproveAndSellAll}
                disabled={isSelling}
              >
                {isSelling ? '处理中...' : '一键授权+全卖'}
              </button>
              <button 
                style={getButtonStyle(isSelling)} 
                onClick={handleSell}
                disabled={isSelling}
              >
                {isSelling ? '处理中...' : '一键授权+卖出指定数量'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={consoleStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ color: '#FF69B4', margin: '0 0 10px 0' }}>控制台输出:</h3>
          <button 
            style={{ 
              ...buttonStyle, 
              backgroundColor: '#FFC0CB',
              padding: '4px 12px',
            }} 
            onClick={() => setConsoleOutput([])}
          >清空日志</button>
        </div>
        {consoleOutput.map((log, index) => (
          <div key={index} style={consoleTextStyle}>{log}</div>
        ))}
      </div>
    </div>
  );
}

export default App;
